import { useState } from 'react';
import { AlertCircle, CalendarRange, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExportarModal } from '@/features/documentos/components/ExportarModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import {
  DetalhesNfseModal,
  FiltroServicosNfseControls,
  NfseListCard,
  ServicosNfseHeader,
  ServicosNfseSummaryCards,
} from './components';
import { useServicosNfsePage } from './components/hooks';

export function ServicosNfsePage() {
  const u = useServicosNfsePage();
  const [showExportarModal, setShowExportarModal] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <ServicosNfseHeader onAplicar={u.handleBuscar} />

      <main className="w-full px-6 py-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="mb-2 text-3xl font-bold text-foreground">Serviços - NFS-e</h2>
            <p className="text-muted-foreground">
              Gerenciamento de Notas Fiscais de Serviços Eletrônicas (Prestados e Tomados) conforme
              NT 003/2025
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowExportarModal(true)}>
              <Download className="mr-2 h-4 w-4" />
              Exportar
            </Button>
          </div>
        </div>

        {!u.buscaAplicada || !u.selecaoEmpresaValida ? (
          <NfseSelecaoObrigatoria selecaoEmpresaValida={u.selecaoEmpresaValida} />
        ) : u.isLoadingPage ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div>
                <p className="font-medium text-foreground">Carregando informações das NFS-e</p>
                <p className="text-sm text-muted-foreground">
                  Aguarde enquanto buscamos os serviços tomados e prestados.
                </p>
              </div>
            </div>
          </div>
        ) : u.hasNoResults ? (
          <NfseEmptyState />
        ) : (
          <>
            <ServicosNfseSummaryCards
              totalServicosTomados={u.totalServicosTomados}
              totalCreditoIBS={u.totalCreditoIBS}
              totalCreditoCBS={u.totalCreditoCBS}
              totalCredito={u.totalCredito}
            />

            <Tabs defaultValue="tomados" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="tomados">
                  Serviços Tomados ({u.totalServicosTomados})
                </TabsTrigger>
                <TabsTrigger value="prestados">
                  Serviços Prestados ({u.totalServicosPrestados})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tomados" className="space-y-6">
                <NfseListCard
                  title="NFS-e Tomadas"
                  description="Notas fiscais de serviços tomados de terceiros"
                  documentos={u.servicosTomados}
                  totalItens={u.totalServicosTomados}
                  isLoading={u.isLoadingTomados}
                  isFetching={u.isFetchingTomados}
                  atualizando={u.atualizando}
                  tipo="tomados"
                  paginaAtual={u.paginacaoTomados.pagina}
                  itensPorPagina={u.paginacaoTomados.itensPorPagina}
                  sortField={u.sortField}
                  sortDirection={u.sortDirection}
                  onAtualizar={u.handleAtualizar}
                  onPaginaChange={u.handlePaginaTomadosChange}
                  onItensPorPaginaChange={u.handleItensPorPaginaTomadosChange}
                  onSelectDocumento={documento => u.handleSelectDocumento(documento, 'tomados')}
                  onSortChange={u.handleSortChange}
                  showAtualizar={false}
                  actions={
                    <FiltroServicosNfseControls
                      filtros={u.filtrosTabela}
                      onChange={u.handleAplicarFiltrosTabela}
                      onLimpar={u.handleLimparFiltrosTabela}
                    />
                  }
                />
              </TabsContent>

              <TabsContent value="prestados" className="space-y-6">
                <NfseListCard
                  title="NFS-e Prestadas"
                  description="Notas fiscais de serviços prestados a terceiros"
                  documentos={u.servicosPrestados}
                  totalItens={u.totalServicosPrestados}
                  isLoading={u.isLoadingPrestados}
                  isFetching={u.isFetchingPrestados}
                  atualizando={u.atualizando}
                  tipo="prestados"
                  paginaAtual={u.paginacaoPrestados.pagina}
                  itensPorPagina={u.paginacaoPrestados.itensPorPagina}
                  sortField={u.sortField}
                  sortDirection={u.sortDirection}
                  onAtualizar={u.handleAtualizar}
                  onPaginaChange={u.handlePaginaPrestadosChange}
                  onItensPorPaginaChange={u.handleItensPorPaginaPrestadosChange}
                  onSelectDocumento={documento => u.handleSelectDocumento(documento, 'prestados')}
                  onSortChange={u.handleSortChange}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      <DetalhesNfseModal
        open={u.showDetalhesModal}
        onOpenChange={u.handleDetalhesModalChange}
        documento={u.selectedDocumento}
        tipo={u.selectedAba ?? undefined}
        onManifestada={u.handleManifestada}
      />

      <ExportarModal
        open={showExportarModal}
        onOpenChange={setShowExportarModal}
        tipoDocumento="0"
        modeloDocumentoDefault="13"
        habilitarDownloadLoteNfse
      />
    </div>
  );
}

function NfseSelecaoObrigatoria({ selecaoEmpresaValida }: { selecaoEmpresaValida: boolean }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold">Selecione Empresa e Mês/Ano</p>
            <p className="text-sm text-muted-foreground">
              {selecaoEmpresaValida
                ? 'Seleção pronta. Clique em Aplicar no header para carregar as NFS-e.'
                : 'Para consultar, selecione uma Empresa e o período no modo Mês/Ano.'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NfseEmptyState() {
  return (
    <section className="max-w-[80%] mx-auto mt-20 relative overflow-hidden rounded-lg border border-black/5 bg-[linear-gradient(180deg,#ffffff_0%,#f7f8fa_100%)] px-8 py-12 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
      <div className="absolute -right-16 top-0 h-48 w-48 rounded-full bg-[#c7f36b]/20 blur-3xl" />
      <div className="absolute -left-12 bottom-0 h-40 w-40 rounded-full bg-[#dbeafe]/40 blur-3xl" />

      <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center">
        <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-[24px] border border-black/5 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.10)]">
          <CalendarRange className="h-9 w-9 text-[#111827]" />
        </div>

        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-black/5 bg-white/80 px-3 py-1 text-xs font-medium text-[#4b5563] backdrop-blur">
          <CalendarRange className="h-3.5 w-3.5" />
          Período atual sem documentos
        </div>

        <h3 className="text-2xl font-semibold tracking-[-0.03em] text-[#111827]">
          Nenhum Resultado Encontrado para o Período Informado
        </h3>

        <p className="mt-4 max-w-2xl text-[15px] leading-7 text-[#6b7280]">
          Não localizamos NFS-e tomadas ou prestadas com os filtros atuais. Amplie o intervalo de
          datas no cabeçalho para buscar um histórico maior e visualizar os documentos disponíveis.
        </p>
      </div>
    </section>
  );
}
